import { Box, Stack } from '@mui/material';
import { SetStateAction, useMemo } from 'react';
import { SidePanelTabs } from 'src/components';
import { MembershipInformation } from 'src/components/SidePanelTabs/MembershipInformation/membership-information';
import { Projects } from 'src/components/SidePanelTabs/Projects/projects';
import { RecentConversations } from 'src/components/SidePanelTabs/RecentConversations/recent-conversations';
import { SiteActivityTab } from 'src/components/SidePanelTabs/site-acitivity-tab/site-activity-tab';
import { SiteClients } from 'src/components/SidePanelTabs/SiteClient/site-clients';
import { SiteDocumentation } from 'src/components/SidePanelTabs/SiteDocumentation/site-documentation';
import { Address, Conversation, Project, useGroupRules } from 'src/graphql';
import { SiteOverview } from 'src/pages/Sites/SiteOverview';
import useSiteViewsStore from 'src/store/site-views';
import { SITE_TABS, SiteTabs } from './site-const';

interface SiteRelatedTabsProps {
  conversation?: Conversation;
  address: Partial<Address>;
  project?: Project;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
}

interface TabContentProps {
  conversation?: Conversation;
  address: Partial<Address>;
  activeTab: SiteTabs;
  project?: Project;
}

const TabContent = ({ activeTab, conversation, address, project }: TabContentProps) => {
  if (activeTab === SiteTabs.SiteDocumentation) {
    return (
      <Box margin={-1}>
        <SiteDocumentation conversation={conversation} address={address} />
      </Box>
    );
  }

  if (activeTab === SiteTabs.Conversations) {
    return <RecentConversations conversation={conversation} address={address} />;
  }

  if (activeTab === SiteTabs.Clients) {
    return <SiteClients conversation={conversation} address={address} />;
  }

  if (activeTab === SiteTabs.MembershipInformation) {
    return <MembershipInformation conversation={conversation} address={address} />;
  }

  if (activeTab === SiteTabs.AiSummary && address.addressId) {
    return (
      <Stack spacing={1} flexGrow={1} minHeight={0}>
        <SiteOverview addressId={address.addressId} />
      </Stack>
    );
  }

  if (activeTab === SiteTabs.Projects && !project) {
    return <Projects conversation={conversation} address={address} />;
  }

  if (activeTab === SiteTabs.Activity) {
    return <SiteActivityTab addressId={address.addressId} partnerId={address.partnerId} />;
  }

  return null;
};

const sidePanelInnerSx = { overflow: 'unset' };

export const SiteRelatedTabs = ({
  conversation,
  address,
  project,
  variant = 'standard',
}: SiteRelatedTabsProps) => {
  const { selectedSiteTab, setSelectedSiteTab } = useSiteViewsStore();
  const { checkValidity, loading } = useGroupRules();

  const tabs = useMemo(() => {
    return SITE_TABS.filter((tab) =>
      tab.value === SiteTabs.Projects ? !project : true,
    ).filter(
      (tab) =>
        !tab.allowedRules ||
        (!loading && checkValidity(tab.allowedRules, { matchType: 'any' })),
    );
  }, [checkValidity, project, loading]);

  return (
    <SidePanelTabs
      key={address?.addressId}
      activeTab={selectedSiteTab}
      setActiveTab={(newValue: SetStateAction<SiteTabs>) => {
        setSelectedSiteTab(newValue as SiteTabs);
      }}
      tabs={tabs}
      variant={variant}
      isNested
      innerSx={sidePanelInnerSx}
    >
      <TabContent
        conversation={conversation}
        activeTab={selectedSiteTab}
        address={address}
        project={project}
      />
    </SidePanelTabs>
  );
};
